import { useEffect, useState, useCallback } from 'react';
import API_BASE_URL from '../Config/config';

/**
 * useEliteTechnologies — the single source of truth for the "Elite
 * Technology Mastery" catalog (GET /api/elite/technologies), replacing the
 * hardcoded ['java-premium', 'python-premium'] lists that used to live
 * independently in EliteTechnologyMastery.jsx, EliteSelectionModal.jsx, and
 * EliteTechnologyDetail.jsx. An admin adding a technology via
 * /superadmin/elite-technologies now shows up here without a redeploy.
 *
 * Returns technologies shaped for the existing UI components:
 *   { id, title, description, image, color, gradient, docsFolder, status,
 *     isComingSoon }
 * `id` is the catalog slug (e.g. 'java-premium') — what gets stored as the
 * user's selection and sent to /api/elite/selection. `docsFolder` is where
 * the actual markdown content lives under /docs/<docsFolder>/mapping.json —
 * kept separate from `id` because content can live in a differently-named
 * folder than the product-facing slug (see the migration's seed data).
 *
 * Progress note: EliteTechnologyMastery.jsx sets `currentHierarchy.technology`
 * to the catalog slug (`tech.id`), and EliteMarkdownViewer.jsx writes
 * completion state to localStorage as `completed_docs_${technology}` — so
 * once a technology exists in this catalog with real content, its reading
 * progress is tracked under `completed_docs_<slug>` automatically, no extra
 * code needed. Components that AGGREGATE progress across technologies
 * (Dashboard.jsx, profile.jsx, CleanProfile.jsx, SidebarTechTree.jsx) still
 * need to know which localStorage keys to read, though — see
 * LEGACY_FREE_TECH_IDS below for the identifiers that predate this catalog
 * and are never returned by /api/elite/technologies.
 */

// Technology identifiers written to localStorage (`completed_docs_<id>`)
// by the pre-catalog free-content system (TopicExplorer.jsx's non-elite
// `contentMap.json` branch uses 'AI'/'AWS'/'React'; its elite branch uses
// bare 'java'/'python' — distinct from the catalog slugs 'java-premium'/
// 'python-premium', which already come back from useEliteTechnologies()).
// These are NOT in the elite_technologies table and never will be, so they
// can't be discovered dynamically — they're kept as a fixed list here
// specifically so progress-aggregating components don't lose data that
// predates the Technology Factory (Phase 1).
export const LEGACY_FREE_TECH_IDS = ['AI', 'AWS', 'React', 'java', 'python'];

export default function useEliteTechnologies() {
  const [technologies, setTechnologies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/elite/technologies`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const mapped = (data.technologies || []).map((t) => ({
        id: t.slug,
        title: t.title,
        description: t.description || '',
        image: t.icon_url,
        color: t.color,
        gradient: t.gradient,
        docsFolder: t.docs_folder,
        status: t.status,
        isComingSoon: t.status === 'coming_soon',
      }));
      setTechnologies(mapped);
    } catch (err) {
      console.error('Failed to load Elite Technologies catalog:', err);
      setError(err);
      setTechnologies([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { technologies, loading, error, reload: load };
}
